import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { flagsEvalGet, featureFlagsEvalSchema } from '@hf/sharedContract';
import { useAccount } from '@core/utils/auth';
import { useHttp } from '@core/utils/http';

/** Client poll interval while signed in (BFF eval always reads Dynamo). */
const POLL_MS = 3 * 60 * 1000;
/** Aborted or dropped evals (Strict Mode remount, a superseded call) must not leave `ready` false. */
const EVAL_ATTEMPTS = 3;

interface FeatureFlagsContextValue {
  /** Evaluated key → boolean for the signed-in user. */
  flags: Record<string, boolean>;
  /** True after the first eval attempt (success or fail-closed). */
  ready: boolean;
  isEnabled: (key: string) => boolean;
  /** Force a poll now (e.g. after admin mutates a flag). */
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

/**
 * App-wide feature-flag store. Mount once under Account + theme (for `useHttp`) inside the router.
 * Re-evals on every pathname change, on a 3‑minute interval, and on focus/visibility while signed in.
 * Concurrent refresh calls share one in-flight request (avoids Strict Mode cancel/retry).
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { user } = useAccount();
  const { pathname } = useLocation();
  const { request } = useHttp();
  const requestRef = useRef(request);
  requestRef.current = request;

  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);
  const userSub = user?.sub ?? null;
  const userSubRef = useRef(userSub);
  userSubRef.current = userSub;
  const inflightRef = useRef<Promise<void> | null>(null);
  const attemptsRef = useRef(0);
  const refreshRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const refresh = useCallback(async () => {
    if (!userSub) {
      setFlags({});
      readyRef.current = false;
      setReady(false);
      attemptsRef.current = 0;
      return;
    }

    if (inflightRef.current) return inflightRef.current;

    const sub = userSub;
    inflightRef.current = (async () => {
      const attempt = ++attemptsRef.current;
      try {
        const res = await requestRef.current(flagsEvalGet.path, {
          responseSchema: featureFlagsEvalSchema,
          toastError: false,
        });
        if (userSubRef.current !== sub) return;
        if (res.status >= 200 && res.status < 300 && res.data) {
          attemptsRef.current = 0;
          setFlags(res.data.flags);
          readyRef.current = true;
          setReady(true);
          return;
        }
        // status 0 is an abort or a dropped connection — not a real eval. Retry before fail-closed.
        if (res.status === 0 && !readyRef.current && attempt < EVAL_ATTEMPTS && userSubRef.current === sub) {
          queueMicrotask(() => {
            if (userSubRef.current === sub && !readyRef.current) void refreshRef.current();
          });
          return;
        }
        if (!readyRef.current) {
          attemptsRef.current = 0;
          setFlags({});
          readyRef.current = true;
          setReady(true);
        }
      } finally {
        inflightRef.current = null;
      }
    })();

    return inflightRef.current;
  }, [userSub]);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!userSub) {
      setFlags({});
      readyRef.current = false;
      setReady(false);
      attemptsRef.current = 0;
      return;
    }
    void refresh();
  }, [pathname, refresh, userSub]);

  useEffect(() => {
    if (!userSub) return;

    const id = window.setInterval(() => void refresh(), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refresh, userSub]);

  const isEnabled = useCallback((key: string) => flags[key] === true, [flags]);

  const value = useMemo(
    () => ({
      flags,
      ready,
      isEnabled,
      refresh,
    }),
    [flags, ready, isEnabled, refresh],
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

/** Read evaluated flags from the app-wide store. */
export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  return ctx;
}
